import { ReactElement } from 'react'
import styled from 'styled-components'
import { breakpoints } from '../../../../../../src/theme/breakpoints'
import { I18n, Translate } from 'common'
import { Hyperlink } from '../../../../../../src/ui-components/util/Hyperlink'
import { InventoryDisplay, ProductLocationModel } from 'interfaces'
import { LocationType } from '../../../../../../src/helpers/locationType'
import { NotInterested, Store, Warning, Error } from '@mui/icons-material'
import WarningLabel from '../../../../../../src/features/WarningLabel'
import { defaultTheme } from '../../../../../../src/theme'

interface Props {
  location: ProductLocationModel
  inStockSomewhere: boolean
  orderIfNotAvailable: boolean
  contactNumber: string
  showLocationSelector: () => void
  invDisplayState: InventoryDisplay
  getNonPrimaryLocationsQtyAvailable: number
  getPrimaryLocationQtyAvailable: {
    qty: number
    called: string
  }
  getOtherLocationsQtyAvailable: number
}

const AvailabilityInfo = (props: Props): ReactElement => {
  const {
    location,
    orderIfNotAvailable,
    contactNumber,
    showLocationSelector,
    invDisplayState,
    inStockSomewhere,
    getNonPrimaryLocationsQtyAvailable,
    getPrimaryLocationQtyAvailable,
    getOtherLocationsQtyAvailable,
  } = props

  const { locationId, called = [], qtyAvailable } = location || {}

  const isPrimary = location?.locType === 'primary'

  return (
    <Wrapper>
      <AvaliabiltyContainer>
        <SubtitleContainer>
          <Subtitle>{Translate('availability')}</Subtitle>
          {inStockSomewhere && (
            <Hyperlink action={showLocationSelector}>
              {!isPrimary ? (
                <SubLocationQtyInfo>
                  <I18n
                    name="atOtherLocations"
                    textReplacement={[getOtherLocationsQtyAvailable]}
                  />
                </SubLocationQtyInfo>
              ) : (
                <I18n name="moreLocations" />
              )}
            </Hyperlink>
          )}
        </SubtitleContainer>
      </AvaliabiltyContainer>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <LocationFontWrapper>
          {invDisplayState === InventoryDisplay.AVAILABLE_AT_LOCATION && (
            <Span className="fw-bold">
              {location?.locType === LocationType.VIEW_ONLY && (
                <StyledWarning theme={defaultTheme} />
              )}
              {qtyAvailable}&nbsp;
              <I18n name="at" />
              &nbsp;
              {Number(locationId) === 100 && (
                <Store htmlColor={defaultTheme.colors.green} />
              )}
              {called}
            </Span>
          )}
          {invDisplayState === InventoryDisplay.SHOW_ZERO && (
            <Span className="fw-bold">
              <NotInterested htmlColor={defaultTheme.colors.red} />
              &nbsp;0&nbsp;
              <I18n name="available" />
              &nbsp;
              <I18n name="at" />
              &nbsp;
              {called}
            </Span>
          )}
          {invDisplayState === InventoryDisplay.CALL_FOR_AVAILABILITY && (
            <>
              <Span className="fw-bold">
                <Error htmlColor={defaultTheme.colors.red} />
                &nbsp;
                <I18n name="outOfStock" />
              </Span>
              <Description>
                <I18n name="callForAvailability" />
                <br />
                {!orderIfNotAvailable &&
                  Translate('pleaseContactForInformation', [contactNumber])}
              </Description>
            </>
          )}
        </LocationFontWrapper>

        {invDisplayState === InventoryDisplay.AVAILABLE_AT_LOCATION && (
          <>
            {isPrimary && (
              <SubLocationQtyInfo>
                <I18n
                  name="atOtherLocations"
                  textReplacement={[getNonPrimaryLocationsQtyAvailable]}
                />
              </SubLocationQtyInfo>
            )}
            {!isPrimary && (
              <SubLocationQtyInfo>
                {getPrimaryLocationQtyAvailable.qty}&nbsp;
                <I18n name="at" />
                &nbsp;
                <Store htmlColor={defaultTheme.colors.greyDark} />
                {getPrimaryLocationQtyAvailable.called}
              </SubLocationQtyInfo>
            )}
          </>
        )}

        {invDisplayState === InventoryDisplay.SHOW_ZERO && (
          <>
            {isPrimary && (
              <SubLocationQtyInfo>
                <I18n
                  name="atOtherLocations"
                  textReplacement={[getNonPrimaryLocationsQtyAvailable]}
                />
              </SubLocationQtyInfo>
            )}
            {!isPrimary && (
              <SubLocationQtyInfo>
                {getPrimaryLocationQtyAvailable.qty}&nbsp;
                <I18n name="at" />
                &nbsp;
                <Store htmlColor={defaultTheme.colors.greyDark} />
                {getPrimaryLocationQtyAvailable.called}
              </SubLocationQtyInfo>
            )}
          </>
        )}
        {location?.locType === LocationType.VIEW_ONLY && (
          <I18n name="viewOnlyLocationMessage" style={{ width: '150px' }} />
        )}
        {location?.locType === LocationType.ORDER_FROM_PRIMARY && (
          <WarningLabel
            name="orderFromPrimaryWarining"
            style={{
              width: '150px',
            }}
          />
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const AvaliabiltyContainer = styled.div`
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
  }
`

const SubtitleContainer = styled.div``

const Subtitle = styled.h4`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  margin-bottom: 3px;
`

const LocationFontWrapper = styled.div`
  span,
  p {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
    color: ${defaultTheme.colors.primaryText};
  }
`

const StyledWarning = styled(Warning)`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 18px;
  margin-top: auto;
  margin-bottom: auto;
  margin-right: 5px;
`

const Span = styled.span`
  white-space: normal;
`

const Description = styled.div`
  font-family: Roboto, serif;
  font-size: 16px;
  line-height: 24px;
  text-align: left;
  margin-top: 1rem;
`

const SubLocationQtyInfo = styled.div`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  font-family: Roboto;
`

export default AvailabilityInfo
